<script lang="ts">
export default {
  name: 'PolicyServicingRolesPanel',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { Undo2 } from '@lucide/vue';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import UIInput from '@/components/ui/UIInput.vue';
import { policyService } from '@/services/v2/PolicyService';
import userService, { type User } from '@/services/UserService';
import { useToast } from '@/composables/useToast';
import type { ServicingRole, EmployeeInfo, AllowedUserForServicingRole } from '@/types/v2/Policy';

const props = defineProps<{
  policyId: number;
  policyLineId?: number;
}>();

const { success, error } = useToast();

const loading = ref(false);
const saving = ref(false);
const roles = ref<ServicingRole[]>([]);
const originalRoles = ref<ServicingRole[]>([]);
const changedIndices = ref<Set<number>>(new Set());
const allUsers = ref<User[]>([]);
const allowedUsers = ref<AllowedUserForServicingRole[]>([]);

const isDisabled = computed(() => loading.value || saving.value);
const hasChanges = computed(() => changedIndices.value.size > 0);

async function load(): Promise<void> {
  loading.value = true;
  try {
    const [r, u, a] = await Promise.all([
      policyService.getPolicyLineServicingRoles(props.policyId, props.policyLineId),
      userService.getAllEmployeeUsers(),
      policyService.getAllowedUsersForServicingRoles(),
    ]);
    roles.value = r;
    originalRoles.value = JSON.parse(JSON.stringify(r)) as ServicingRole[];
    allUsers.value = u;
    allowedUsers.value = a;
    changedIndices.value = new Set();
  } catch (e) {
    console.error('Error loading servicing roles', e);
    roles.value = [];
    originalRoles.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(load);

watch(
  () => [props.policyId, props.policyLineId],
  () => {
    load();
  },
);

function isInactive(emp: EmployeeInfo): boolean {
  return emp.accountStatus === 'Inactive';
}

function userOptionsForRole(role: ServicingRole): ComboboxOption[] {
  const allowedIds = allowedUsers.value.length
    ? allowedUsers.value.filter((m) => m.servicingRoleCode === role.code).map((m) => m.userId)
    : allUsers.value.map((u) => u.id);
  const opts: ComboboxOption[] = allUsers.value
    .filter((u) => allowedIds.includes(u.id))
    .map((u) => ({ label: u.name || u.userName || u.userEmail, value: u.sourceEmployeeKey }));

  // Preserve current inactive employee in the list so the value renders
  if (isInactive(role.employeeInfo) && role.employeeInfo.lookupCode) {
    const present = opts.some((o) => o.value === role.employeeInfo.lookupCode);
    if (!present) {
      opts.push({
        label: `${role.employeeInfo.firstName} ${role.employeeInfo.lastName}`,
        value: role.employeeInfo.lookupCode,
      });
    }
  }
  return opts;
}

function handleEmployeeChange(index: number, newCode: string): void {
  const selected = allUsers.value.find((u) => u.sourceEmployeeKey === newCode);
  if (selected && newCode) {
    roles.value[index].employeeInfo = {
      lookupCode: newCode,
      firstName: selected.name?.split(' ')[0] || '',
      lastName: selected.name?.split(' ').slice(1).join(' ') || '',
      accountStatus: 'Active',
      inactiveReason: '',
    };
    roles.value[index].published = true;
  } else {
    roles.value[index].employeeInfo.lookupCode = newCode;
  }
  recomputeChange(index);
}

function handlePublishedChange(index: number): void {
  recomputeChange(index);
}

function recomputeChange(index: number): void {
  const orig = originalRoles.value[index];
  const cur = roles.value[index];
  const empChanged = (orig?.employeeInfo?.lookupCode || '') !== (cur.employeeInfo.lookupCode || '');
  const pubChanged = (orig?.published ?? true) !== (cur.published ?? true);
  if (empChanged || pubChanged) {
    changedIndices.value.add(index);
  } else {
    changedIndices.value.delete(index);
  }
  changedIndices.value = new Set(changedIndices.value);
}

function revert(index: number): void {
  const orig = originalRoles.value[index];
  if (!orig) return;
  roles.value[index].employeeInfo = JSON.parse(JSON.stringify(orig.employeeInfo));
  roles.value[index].published = orig.published ?? true;
  changedIndices.value.delete(index);
  changedIndices.value = new Set(changedIndices.value);
}

async function save(): Promise<void> {
  if (!hasChanges.value) return;
  saving.value = true;
  try {
    const updates = Array.from(changedIndices.value).map((idx) => {
      const r = roles.value[idx];
      const orig = originalRoles.value[idx];
      return {
        code: r.code,
        employeeLookupCode: r.employeeInfo.lookupCode,
        published: r.published ?? true,
        epicChanges: (orig?.employeeInfo?.lookupCode || '') !== (r.employeeInfo.lookupCode || ''),
      };
    });
    await policyService.updatePolicyLineServicingRoles(props.policyId, props.policyLineId, updates);
    originalRoles.value = JSON.parse(JSON.stringify(roles.value)) as ServicingRole[];
    changedIndices.value = new Set();
    success('Servicing roles saved');
  } catch (e) {
    console.error('Error saving servicing roles', e);
    error('Error saving servicing roles');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="loading"
      class="flex h-32 items-center justify-center">
      <span class="loading loading-md loading-spinner"></span>
    </div>
    <div
      v-else-if="roles.length === 0"
      class="rounded border border-base-300 p-6 text-center text-sm text-slate-medium">
      No servicing roles found.
    </div>
    <template v-else>
      <div
        v-for="(role, index) in roles"
        :key="role.code"
        class="rounded border border-base-300 p-3">
        <div class="text-sm font-medium text-slate-dark">{{ role.role }}</div>
        <div
          v-if="role.description"
          class="mb-2 text-xs text-slate-medium">
          {{ role.description }}
        </div>
        <div class="flex items-center gap-3">
          <div class="w-64">
            <template
              v-if="
                role.employeeInfo.lookupCode &&
                !isInactive(role.employeeInfo) &&
                !userOptionsForRole(role).some((o) => o.value === role.employeeInfo.lookupCode)
              ">
              <UIInput
                :model-value="role.employeeInfo.lookupCode"
                disabled
                class="input-sm w-full border-warning" />
            </template>
            <template v-else>
              <Combobox
                :model-value="role.employeeInfo.lookupCode"
                @update:model-value="(value) => handleEmployeeChange(index, value)"
                :options="userOptionsForRole(role)"
                :disabled="isDisabled"
                placeholder="Select a user..."
                :input-class="`input-sm w-full ${
                  isInactive(role.employeeInfo) ? 'border-error' : changedIndices.has(index) ? 'border-success' : ''
                }`" />
            </template>
          </div>
          <label
            v-if="role.employeeInfo.lookupCode"
            class="flex items-center gap-1">
            <input
              type="checkbox"
              v-model="role.published"
              @change="handlePublishedChange(index)"
              class="toggle border border-solid border-base-300 toggle-primary toggle-sm"
              :disabled="isDisabled" />
            <span class="text-xs text-slate-medium">Client Visible</span>
          </label>
          <button
            v-if="changedIndices.has(index)"
            class="btn text-slate-medium btn-xs hover:text-slate-dark"
            title="Undo change"
            @click="revert(index)">
            <Undo2 class="h-4 w-4" />
          </button>
        </div>
        <div
          v-if="isInactive(role.employeeInfo)"
          class="mt-1 text-xs text-error">
          {{ role.employeeInfo.firstName }} {{ role.employeeInfo.lastName }}
          <span v-if="role.employeeInfo.inactiveReason">({{ role.employeeInfo.inactiveReason }})</span>
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <button
          class="btn text-white btn-sm btn-primary"
          :disabled="!hasChanges || isDisabled"
          @click="save">
          <span
            v-if="saving"
            class="loading loading-xs loading-dots"></span>
          <span v-else>Save servicing roles</span>
        </button>
      </div>
    </template>
  </div>
</template>
